import type { GlobalFilterFn } from '../types/table.types';

/**
 * Props for the DataTable.GlobalFilter configuration component.
 *
 * Supports two modes:
 * - **Controlled**: Use `value` + `onChange` - parent owns the state
 * - **Uncontrolled**: Use `initial` + optional `onChange` - table owns the state
 */
export interface GlobalFilterProps<TData = unknown> {
  /**
   * Controlled mode: Current global filter value (free-text search string).
   *
   * @example
   * ```tsx
   * const [search, setSearch] = useState('');
   * <DataTable.GlobalFilter value={search} onChange={setSearch} />
   * ```
   */
  value?: string;

  /**
   * Uncontrolled mode: Initial global filter value applied on mount.
   * Only used when `value` is not provided.
   */
  initial?: string;

  /**
   * Called when the global filter changes from any source.
   * Works in both controlled and uncontrolled modes.
   */
  onChange?: (value: string) => void;

  /**
   * Custom global filter function. If omitted, the table uses a default
   * case-insensitive substring match across all visible cell values.
   */
  filterFn?: GlobalFilterFn<TData>;
}

/**
 * Configuration component for global (cross-column) text filtering.
 *
 * The search input UI is rendered by the consumer; this component is purely
 * declarative and reports the value to the table so rows can be filtered.
 * Mirrors the API of `DataTable.Filter` and `DataTable.Sorting`.
 *
 * @example Controlled with external search input
 * ```tsx
 * const [search, setSearch] = useState('');
 *
 * <input value={search} onChange={(e) => setSearch(e.target.value)} />
 *
 * <DataTable id="users" data={users}>
 *   <DataTable.GlobalFilter value={search} onChange={setSearch} />
 *   <DataTable.Column id="name" accessor="name" />
 * </DataTable>
 * ```
 */
export const GlobalFilter = <TData,>(props: GlobalFilterProps<TData>): null => {
  if (import.meta.env.DEV) {
    if (props.value !== undefined && props.initial !== undefined) {
      console.warn(
        'DataTable.GlobalFilter: `value` and `initial` are mutually exclusive. ' +
          'Use `value` for controlled mode or `initial` for uncontrolled mode, not both.'
      );
    }
    if (props.value !== undefined && props.onChange === undefined) {
      console.warn(
        'DataTable.GlobalFilter: Using `value` without `onChange` makes the global filter read-only. ' +
          'User interactions will not update the filter. Provide an `onChange` handler.'
      );
    }
  }
  return null;
};

GlobalFilter.displayName = 'DataTable.GlobalFilter';
